import { ActivitySpinnerWidget, TextFieldWidget } from '@tableau/widgets';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { CheckboxState } from '~src/commonInfra/components/checkbox/CheckboxState';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { ISliceable } from '~src/httpClient/fetchers/ISliceable';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { IGroup } from '~src/userManagement/groups/serverApi/types/IGroup';

import { IGroupSetGroupInfo, IGroupSetInfo } from '../models/IGroupSetInfo';
import { AddGroupsToGroupSetGrid } from './AddGroupsToGroupSetGrid';
import { textFieldStyles } from './styles/AddGroupsToGroupSetDialogStyles';

export const INITIAL_SLICE_SIZE = 100;
export const SCROLL_SLICE_SIZE = 50;

export namespace AddGroupsToGroupSetDialogTestIds {
    export const labelTestId = 'add-groups-label-';
    export const searchFieldTestId = 'add-groups-groupname-filter';
    export const errorTestId = 'add-groups-error-';
    export const noResultsTestId = 'add-groups-no-results';
    export const activitySpinnerTestId = 'add-groups-activity-spinner';
}

export type AddGroupsToGroupSetDialogProps = {
    readonly groupSet: IGroupSetInfo;
    getQuery: (searchString: string) => ISliceable<IGroup>;
    inGroupSetGroupsList: IGroupSetGroupInfo[];
}


export type AddGroupsToGroupSetDialogState = {
    selectedGroupIds: string[];
    selectedIGroupInfoSet: Set<IGroup>;
}

export type AddGroupsToGroupSetActionDialogProps = ReactActionDialogBodyProps<AddGroupsToGroupSetDialogProps, AddGroupsToGroupSetDialogState>;

export function calculateAddableResultsList(resultsList: IGroup[], inGroupList: IGroupSetGroupInfo[]) {
    // return list of resultsList minus items in inGroupList
    let addableGroups: IGroup[] = [];
    resultsList.forEach(groupFromResults => {
      if (inGroupList.findIndex(groupFromGroupSet => groupFromResults.id === groupFromGroupSet.id) === -1) {
        addableGroups.push(groupFromResults);
      }
    });
    return addableGroups;
}

export function calculateIUserInfoSet(rowCheckboxesState: _.Dictionary<string>, addableUsers: IGroup[]): Set<IGroup> {
    let userSet: Set<IGroup> = new Set();
    addableUsers.forEach((user: IGroup) => {
      if (rowCheckboxesState[user.id] === CheckboxState.checked) {
        userSet.add(user as IGroup);
      }
    });
    return userSet;
}

function spinnerChild(): JSX.Element {
    return (
      <div className={'tb-data-grid-empty'}>
        <ActivitySpinnerWidget testId={AddGroupsToGroupSetDialogTestIds.activitySpinnerTestId} shouldShowUnderlay={false}/>
      </div>
    );
}

export function emptyChild(): JSX.Element{
    return (
      <div className={'tb-data-grid-empty'}>
        <div className={'tb-data-grid-empty-title'} data-tb-test-id={AddGroupsToGroupSetDialogTestIds.noResultsTestId}>
          {Messages.noMatchingResults()}
        </div>
      </div>
    );
}

function errorChild(): JSX.Element{
    return (
      <div className={'tb-data-grid-error'}>
        <div className={'tb-data-grid-error-title'} data-tb-test-id={AddGroupsToGroupSetDialogTestIds.errorTestId}>
          {Messages.errorUnknown()}
        </div>
      </div>
    );
}

export function AddGroupsToGroupSetDialogBody(props: AddGroupsToGroupSetActionDialogProps): JSX.Element {
    const [searchText, setSearchText] = React.useState(''); // the contents of the search field that the user can set
    const [isError, setIsError] = React.useState(false); // flag to track error state
    const [doneInitialFetching, setDoneInitialFetching] = React.useState(false); // flag to track when initial fetching is done
    const [remoteResultCount, setRemoteResultCount] = React.useState(0); // total count ON THE SERVER of results of a particular filtering of groups
    const [localResultCount, setLocalResultCount] = React.useState(0); // count ON THE CLIENT of downloaded results
    const [getNextPage, setGetNextPage] = React.useState(false); // flag that is true when scroll in datagrid bottoms out and needs more data
    const [searchResults, setSearchResults] = React.useState([] as IGroup[]); // the results that are displayed in the grid
    const [allowSliceFetchLock, setAllowSliceFetchLock] = React.useState(true); // lock claimed when false, so slice doesn't concurrently refetch
    const [rowCheckboxesState, setRowCheckboxesState] = React.useState({} as _.Dictionary<string>); // maps user id to CheckboxState

    const fetchFirstChunkOfUsers = async () => {
        // fetch first bunch
        const usersResult: ListResult<IGroup> = await props.getQuery(searchText)
          .getSlice(0, INITIAL_SLICE_SIZE);
        setSearchResults(_ => usersResult.items);
        // calculate total number and save it
        setRemoteResultCount(_ => usersResult.total);
        // calculate local result size and save it
        setLocalResultCount(_ => usersResult.items.length);
        // when done running this hook, set done fetching to true
        setDoneInitialFetching(_ => true);
    };

    React.useEffect(() => {
        // set done fetching to false
        setDoneInitialFetching(_ => false);
        try {
          fetchFirstChunkOfUsers();
        } catch (e) {
          // if error, set isError
          setIsError(_ => true);
        }
      },
        // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchText] // list search params here at the end
    );

    React.useEffect(
        () => {
          //  fetch if slicelock is released and there are more results to grab
          if (remoteResultCount !== localResultCount && allowSliceFetchLock) {
            try {
              setAllowSliceFetchLock(_ => false);
              //fetchNextChunkOfUsers();
            } catch (e) {
              // if error, set isError
              setIsError(_ => true);
            }
          }
        },
        // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [getNextPage]
      );

    return <>
        <div data-tb-test-id={AddGroupsToGroupSetDialogTestIds.labelTestId}>
          {Messages.addGroupsToGroupSetDescription({GROUPSET_NAME: props.groupSet.name})}
        </div>
        <TextFieldWidget
            testId={AddGroupsToGroupSetDialogTestIds.searchFieldTestId}
            placeholder={Messages.searchGroups()}
            kind={'search'}
            label={null}
            className={textFieldStyles}
            style={{width:'100%'}}
            onChange={(e) => {
                setSearchText(e.target.value);
            }}
            message={'\u200B'}
        />
        <AddGroupsToGroupSetGrid
          emptyChild={isError? errorChild() : doneInitialFetching ? emptyChild() : spinnerChild()}
          groupList={!isError && doneInitialFetching ? searchResults : []}
          inGroupSetGroupsList={props.inGroupSetGroupsList}
          rowCheckboxState={
            // A dictionary mapping IGroupInfo.id (a string) to "checked" if the group is selected
            rowCheckboxesState
          }
          handleRowCheckboxChange={handleRowCheckboxChange}
          shouldAskForNextPage={(dataGridEndIndex: number): boolean => {
            // AddGroupsToGroupSetGrid will ask for another page if all the following conditions are true
            //   1. reach end of scroll in grid (dataGridEndIndex + 1 === localCount)
            //   2. there is more data to fetch from server (localCount < remoteCount)
            return dataGridEndIndex + 1 === localResultCount && localResultCount < remoteResultCount ;
          }}
          askForNextPage={() => {
            setGetNextPage(_ => true);
          }}
        />
    </>;

    function handleRowCheckboxChange(rowUser: IGroup): void {
        const checkboxGroupId = rowUser.id;
        const thisRowCheckboxState = rowCheckboxesState[checkboxGroupId];
        const addableGroups = calculateAddableResultsList(searchResults, props.inGroupSetGroupsList);
        let newRowCheckboxesState = rowCheckboxesState;
        if (thisRowCheckboxState === CheckboxState.checked) {
        delete newRowCheckboxesState[checkboxGroupId];
        } else {
        newRowCheckboxesState[checkboxGroupId] = CheckboxState.checked;
        }
        setRowCheckboxesState(_ => newRowCheckboxesState);
        // set header checkbox state
        props.updateDialogState(previousState => {
        return {
            ...previousState,
            selectedGroupIds: Object.keys(newRowCheckboxesState),
            selectedIGroupInfoSet: calculateIUserInfoSet(newRowCheckboxesState, addableGroups)
        };
        });
    }
}